import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { getFromLocalStorage } from '../mock';
import {
  Wallet, Car, CheckCircle, Clock, Eye, Calendar, Shield, DollarSign,
  ChevronRight, Camera, ClipboardCheck, Fuel, AlertTriangle, CircleDot,
  Phone, X, ArrowLeft, Package, FileText, History, Tag, Info, Wrench, LogOut
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { saveToLocalStorage, removeFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend

const STATUS_FLOW = [
  { key: 'beklemede', label: 'Beklemede', color: '#6b7280' },
  { key: 'randevu_planlandi', label: 'Randevu Planlandi', color: '#6366f1' },
  { key: 'arac_kabul', label: 'Arac Kabul', color: '#0891b2' },
  { key: 'islemde', label: 'Islemde', color: '#3b82f6' },
  { key: 'kalite_kontrol', label: 'Kalite Kontrol', color: '#ca8a04' },
  { key: 'teslime_hazir', label: 'Teslime Hazir', color: '#22c55e' },
  { key: 'teslim_edildi', label: 'Teslim Edildi', color: '#374151' },
];

const normalizeStatus = (s) => {
  const map = { pending: 'beklemede', in_progress: 'islemde', ready: 'teslime_hazir', completed: 'teslim_edildi' };
  return map[s] || s;
};
const getStatusIndex = (status) => { const n = normalizeStatus(status); const i = STATUS_FLOW.findIndex(s => s.key === n); return i >= 0 ? i : 0; };
const getStatusLabel = (status) => { const n = normalizeStatus(status); const f = STATUS_FLOW.find(s => s.key === n); return f ? f.label : status; };
const getStatusColor = (status) => { const n = normalizeStatus(status); const f = STATUS_FLOW.find(s => s.key === n); return f ? f.color : '#6b7280'; };

const conditionLabels = { good: 'Iyi', fair: 'Orta', poor: 'Kotu' };
const severityLabels = { none: 'Yok', minor: 'Hafif', moderate: 'Orta', severe: 'Agir' };

const warrantyStatusLabels = { active: 'Aktif', expiring_soon: 'Yakinda Bitiyor', expired: 'Bitti' };
const warrantyStatusColors = { active: 'bg-green-600', expiring_soon: 'bg-yellow-600', expired: 'bg-red-600' };

/* ============ Sub-components ============ */

const StatusTimeline = ({ status, statusHistory }) => {
  const currentIdx = getStatusIndex(status);
  return (
    <div className="py-4" data-testid="status-timeline">
      <div className="flex items-center justify-between relative">
        <div className="absolute top-4 left-6 right-6 h-0.5 bg-gray-700" />
        <div className="absolute top-4 left-6 h-0.5 bg-gradient-to-r from-brand to-green-500 transition-all duration-500"
          style={{ width: `${Math.min(100, (currentIdx / (STATUS_FLOW.length - 1)) * 100)}%`, maxWidth: 'calc(100% - 48px)' }} />
        {STATUS_FLOW.map((step, i) => {
          const isCompleted = i <= currentIdx;
          const isCurrent = i === currentIdx;
          const historyEntry = (statusHistory || []).find(h => normalizeStatus(h.status) === step.key);
          return (
            <div key={step.key} className="flex flex-col items-center z-10 relative" style={{ flex: 1 }}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isCurrent ? 'ring-2 ring-offset-2 ring-offset-gray-900' : ''}`}
                style={{ backgroundColor: isCompleted ? step.color : '#374151', color: isCompleted ? '#fff' : '#6b7280', ringColor: isCurrent ? step.color : undefined }}>
                {isCompleted ? (i < currentIdx ? <CheckCircle className="h-4 w-4" /> : (i + 1)) : (i + 1)}
              </div>
              <span className={`text-[10px] mt-1.5 text-center leading-tight max-w-[70px] ${isCurrent ? 'text-white font-semibold' : isCompleted ? 'text-gray-300' : 'text-gray-600'}`}>{step.label}</span>
              {historyEntry && (
                <span className="text-[9px] text-gray-500 mt-0.5">{new Date(historyEntry.timestamp).toLocaleDateString('tr-TR')}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PhotoGallery = ({ photos, token, title }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
        <Camera className="h-4 w-4 text-brand" />{title} ({photos.length})
      </h4>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {photos.map(p => (
          <div key={p.id} className="aspect-square rounded-lg overflow-hidden cursor-pointer border border-gray-700 hover:border-brand transition-all"
            onClick={() => setLightbox(p)} data-testid={`photo-${p.id}`}>
            <img src={`${BACKEND_URL}/api/photos/file/${p.id}?auth=${token}`} alt={p.description || p.category}
              className="w-full h-full object-cover" loading="lazy" />
          </div>
        ))}
      </div>
      {lightbox && (
        <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl p-2">
            <DialogHeader className="p-2"><DialogTitle className="text-white text-sm">{lightbox.description || lightbox.category}</DialogTitle></DialogHeader>
            <img src={`${BACKEND_URL}/api/photos/file/${lightbox.id}?auth=${token}`} alt={lightbox.description || ''} className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const InspectionSummary = ({ inspection }) => {
  if (!inspection) return null;
  return (
    <div className="space-y-3 mt-4" data-testid="inspection-summary">
      <h4 className="text-white text-sm font-semibold flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-purple-500" />Arac Muayene Ozeti
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {inspection.mileage && (
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-gray-400 text-xs">Kilometre</p>
            <p className="text-white font-bold">{inspection.mileage?.toLocaleString()} km</p>
          </div>
        )}
        {inspection.fuelLevel && (
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-gray-400 text-xs flex items-center gap-1"><Fuel className="h-3 w-3" />Yakit</p>
            <p className="text-white font-bold">{inspection.fuelLevel}</p>
          </div>
        )}
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <p className="text-gray-400 text-xs">Ic Mekan</p>
          <p className="text-white font-bold">{conditionLabels[inspection.interiorCondition] || inspection.interiorCondition}</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <p className="text-gray-400 text-xs">Boya</p>
          <p className="text-white font-bold">{conditionLabels[inspection.paintCondition] || inspection.paintCondition}</p>
        </div>
      </div>
      {inspection.exteriorDamages?.length > 0 && inspection.exteriorDamages.some(d => d.severity !== 'none') && (
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <p className="text-gray-400 text-xs mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Mevcut Hasarlar</p>
          <div className="space-y-1">
            {inspection.exteriorDamages.filter(d => d.severity !== 'none').map((d, i) => (
              <div key={`dmg-${d.location}-${i}`} className="flex justify-between text-sm">
                <span className="text-white">{d.location}</span>
                <Badge className={`text-xs ${d.severity === 'severe' ? 'bg-red-600' : d.severity === 'moderate' ? 'bg-yellow-600' : 'bg-brand'} text-white`}>
                  {severityLabels[d.severity] || d.severity}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      {inspection.warningLights?.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
          <p className="text-gray-400 text-xs mb-2 flex items-center gap-1"><CircleDot className="h-3 w-3" />Uyari Isiklari</p>
          <div className="flex flex-wrap gap-1">{inspection.warningLights.map((w, i) => <Badge key={`wl-${w}-${i}`} className="bg-red-900/50 text-red-400 text-xs">{w}</Badge>)}</div>
        </div>
      )}
    </div>
  );
};

const WarrantyBadge = ({ warranty }) => {
  if (!warranty) return null;
  return (
    <Badge className={`${warrantyStatusColors[warranty.status] || 'bg-gray-600'} text-white text-xs`} data-testid={`warranty-badge-${warranty.status}`}>
      <Shield className="h-3 w-3 mr-1" />{warrantyStatusLabels[warranty.status] || warranty.status}
      {warranty.status === 'active' && ` (${warranty.daysLeft} gun)`}
    </Badge>
  );
};

const ServiceDetailCard = ({ sd, warranty }) => (
  <div className="bg-gray-800/80 rounded-lg p-4 border border-gray-700/50" data-testid="service-detail-card">
    <div className="flex justify-between items-start mb-2">
      <h5 className="text-brand font-semibold text-sm">{sd.name}</h5>
      {warranty && <WarrantyBadge warranty={warranty} />}
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mt-2">
      {sd.installationDate && (
        <div><span className="text-gray-400">Yapim Tarihi:</span><p className="text-white font-medium">{new Date(sd.installationDate).toLocaleDateString('tr-TR')}</p></div>
      )}
      {sd.warrantyYears > 0 && (
        <div><span className="text-gray-400">Garanti Suresi:</span><p className="text-white font-medium">{sd.warrantyYears} Yil</p></div>
      )}
      {warranty && warranty.endDate && (
        <div><span className="text-gray-400">Garanti Bitis:</span><p className="text-white font-medium">{new Date(warranty.endDate).toLocaleDateString('tr-TR')}</p></div>
      )}
    </div>
    {(sd.brand || sd.product || sd.material) && (
      <div className="grid grid-cols-3 gap-2 text-xs mt-3 pt-3 border-t border-gray-700/50">
        {sd.brand && <div><span className="text-gray-400 flex items-center gap-1"><Tag className="h-3 w-3" />Marka</span><p className="text-white font-medium">{sd.brand}</p></div>}
        {sd.product && <div><span className="text-gray-400 flex items-center gap-1"><Package className="h-3 w-3" />Urun</span><p className="text-white font-medium">{sd.product}</p></div>}
        {sd.material && <div><span className="text-gray-400 flex items-center gap-1"><Wrench className="h-3 w-3" />Malzeme</span><p className="text-white font-medium">{sd.material}</p></div>}
      </div>
    )}
    {sd.notes && (
      <p className="text-gray-400 text-xs mt-2 italic">{sd.notes}</p>
    )}
  </div>
);

const PaymentSummary = ({ job }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="payment-summary">
    <div className="bg-gray-800/80 rounded-lg p-3 text-center border border-gray-700/50">
      <p className="text-xs text-gray-400">Toplam</p>
      <p className="text-white font-bold font-mono text-sm">{(job.totalPrice || 0).toLocaleString('tr-TR')} TL</p>
    </div>
    <div className="bg-gray-800/80 rounded-lg p-3 text-center border border-gray-700/50">
      <p className="text-xs text-gray-400">Odenen</p>
      <p className="text-green-500 font-bold font-mono text-sm">{(job.paidAmount || 0).toLocaleString('tr-TR')} TL</p>
    </div>
    <div className="bg-gray-800/80 rounded-lg p-3 text-center border border-gray-700/50">
      <p className="text-xs text-gray-400">Kalan</p>
      <p className={`font-bold font-mono text-sm ${(job.remainingBalance || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
        {(job.remainingBalance || 0).toLocaleString('tr-TR')} TL
      </p>
    </div>
  </div>
);

const ControlAppointmentsList = ({ controls }) => {
  if (!controls || controls.length === 0) return null;
  const statusMap = { planned: 'Planlandi', reminded: 'Hatirlandi', attended: 'Geldi', missed: 'Gelmedi' };
  const statusColorMap = { planned: 'bg-blue-600', reminded: 'bg-yellow-600', attended: 'bg-green-600', missed: 'bg-red-600' };
  return (
    <div className="mt-4" data-testid="control-appointments">
      <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-blue-500" />Kontrol Randevulari
      </h4>
      <div className="space-y-2">
        {controls.map(c => (
          <div key={c.id} className="bg-gray-800/80 rounded-lg p-3 border border-gray-700/50 flex items-center justify-between">
            <div>
              <p className="text-white text-sm">{new Date(c.plannedDate).toLocaleDateString('tr-TR')}</p>
              {c.notes && <p className="text-gray-400 text-xs mt-0.5">{c.notes}</p>}
              {c.observations && <p className="text-gray-300 text-xs mt-0.5">Gozlem: {c.observations}</p>}
              {c.outcome && <p className="text-gray-300 text-xs mt-0.5">Sonuc: {c.outcome}</p>}
            </div>
            <Badge className={`${statusColorMap[c.status] || 'bg-gray-600'} text-white text-xs`}>
              {statusMap[c.status] || c.status}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============ Job Detail Modal ============ */
const JobDetailModal = ({ job, inspection, jobPhotos, token, controls, onClose }) => {
  if (!job) return null;

  const getPhotos = (category) => {
    if (!category) return jobPhotos;
    return (jobPhotos || []).filter(p => p.category?.startsWith(category));
  };

  const jobControls = (controls || []).filter(c => c.jobId === job.id);

  // Normalize: resolve effective service list from all possible sources
  const effectiveServiceDetails = job.serviceDetails?.length > 0 ? job.serviceDetails : [];
  const effectiveServiceNames = job.services?.length > 0
    ? job.services
    : effectiveServiceDetails.map(sd => sd.name).filter(Boolean);
  const hasDetailedServices = effectiveServiceDetails.length > 0;
  const hasAnyServices = effectiveServiceNames.length > 0;

  return (
    <Dialog open={!!job} onOpenChange={() => onClose()}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 z-10">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" data-testid="job-detail-title">
              <Wrench className="h-5 w-5 text-brand" />
              Is Detayi
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className="text-white text-xs" style={{ backgroundColor: getStatusColor(job.status) }}>
              {getStatusLabel(job.status)}
            </Badge>
            <span className="text-gray-400 text-xs">{job.carPlate}</span>
            {job.carDetails && <span className="text-gray-500 text-xs">({job.carDetails})</span>}
            {job.startDate && <span className="text-gray-500 text-xs">{new Date(job.startDate).toLocaleDateString('tr-TR')}</span>}
          </div>
        </div>

        <div className="p-4 space-y-5">
          {/* Timeline */}
          <StatusTimeline status={job.status} statusHistory={job.statusHistory} />

          {/* Payment */}
          <PaymentSummary job={job} />

          {/* Services Section — handles both detailed and legacy */}
          {hasDetailedServices ? (
            <div data-testid="services-section">
              <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <Package className="h-4 w-4 text-brand" />Servisler ve Urun Detaylari
              </h4>
              <div className="space-y-2">
                {effectiveServiceDetails.map((sd, i) => {
                  const w = (job.warrantyInfo || []).find(wi => wi.serviceName === sd.name);
                  return <ServiceDetailCard key={sd.name || `sd-${i}`} sd={sd} warranty={w} />;
                })}
              </div>
            </div>
          ) : hasAnyServices ? (
            <div data-testid="services-section-legacy">
              <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <Package className="h-4 w-4 text-brand" />Yapilan Servisler
              </h4>
              <div className="bg-gray-800/80 rounded-lg p-4 border border-gray-700/50">
                <div className="flex flex-wrap gap-2">
                  {effectiveServiceNames.map((s, i) => (
                    <Badge key={`legacy-svc-${i}`} className="bg-brand/20 text-brand border border-brand/30 text-xs">
                      {s}
                    </Badge>
                  ))}
                </div>
                {job.warrantyYears > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-700/50 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-500" />
                    <span className="text-gray-300 text-sm">Garanti: {job.warrantyYears} Yil</span>
                    {job.installationDate && (
                      <span className="text-gray-500 text-xs ml-2">
                        (Yapim: {new Date(job.installationDate).toLocaleDateString('tr-TR')})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Warranty Summary — from computed warrantyInfo */}
          {job.warrantyInfo?.length > 0 && (
            <div data-testid="warranty-section">
              <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-500" />Garanti Durumu
              </h4>
              <div className="space-y-2">
                {job.warrantyInfo.map((w, i) => (
                  <div key={w.serviceName || `w-${i}`} className="bg-gray-800/80 rounded-lg p-3 border border-gray-700/50 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">{w.serviceName}</p>
                      <p className="text-gray-400 text-xs">
                        {new Date(w.startDate).toLocaleDateString('tr-TR')} - {new Date(w.endDate).toLocaleDateString('tr-TR')}
                        ({w.years} yil)
                      </p>
                    </div>
                    <WarrantyBadge warranty={w} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Notes */}
          {job.serviceNotes && (
            <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-700/30" data-testid="service-notes">
              <h4 className="text-blue-400 text-sm font-semibold mb-1 flex items-center gap-2">
                <Info className="h-4 w-4" />Servis Notlari
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{job.serviceNotes}</p>
            </div>
          )}

          {/* Customer Notes */}
          {job.customerNotes && (
            <div className="bg-green-900/20 rounded-lg p-4 border border-green-700/30" data-testid="customer-notes">
              <h4 className="text-green-400 text-sm font-semibold mb-1 flex items-center gap-2">
                <FileText className="h-4 w-4" />Bakim Onerileri ve Notlar
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{job.customerNotes}</p>
            </div>
          )}

          {/* Inspection */}
          <InspectionSummary inspection={inspection} />

          {/* Control Appointments */}
          <ControlAppointmentsList controls={jobControls} />

          {/* Photos */}
          <PhotoGallery photos={getPhotos('inspection')} token={token} title="Muayene Fotograflari" />
          <PhotoGallery photos={getPhotos('completion')} token={token} title="Teslim Fotograflari" />
          <PhotoGallery photos={getPhotos('general')} token={token} title="Diger Fotograflar" />

          {/* Status History */}
          {job.statusHistory?.length > 0 && (
            <div data-testid="status-history">
              <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                <History className="h-4 w-4 text-gray-400" />Islem Gecmisi
              </h4>
              <div className="space-y-1">
                {[...job.statusHistory].reverse().map((h, i) => (
                  <div key={`sh-${h.status}-${h.timestamp}`} className="flex items-center gap-3 text-xs py-1.5 border-b border-gray-800 last:border-0">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatusColor(h.status) }} />
                    <span className="text-white">{getStatusLabel(h.status)}</span>
                    <span className="text-gray-500 ml-auto">{new Date(h.timestamp).toLocaleString('tr-TR')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============ Main Dashboard ============ */
const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  const handleExitImpersonation = () => {
    const adminToken = getFromLocalStorage('adminToken');
    const adminUser = getFromLocalStorage('adminUser');
    if (adminToken && adminUser) {
      saveToLocalStorage('accessToken', adminToken);
      saveToLocalStorage('currentUser', adminUser);
      removeFromLocalStorage('adminToken');
      removeFromLocalStorage('adminUser');
      removeFromLocalStorage('impersonating');
      navigate('/admin');
    }
  };

  const handleLogout = () => {
    removeFromLocalStorage('accessToken');
    removeFromLocalStorage('currentUser');
    navigate('/login');
  };

  useEffect(() => {
    const currentUser = getFromLocalStorage('currentUser');
    const impersonating = getFromLocalStorage('impersonating');
    if (!currentUser) { navigate('/login'); return; }
    if (currentUser.isAdmin && !impersonating) { navigate('/login'); return; }
    if (impersonating) setIsImpersonating(true);
    const accessToken = getFromLocalStorage('accessToken');
    setToken(accessToken);

    const loadData = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${accessToken}` };
        const res = await fetch(`${BACKEND_URL}/api/customers/me/portal-detail`, { headers });
        if (res.ok) {
          const data = await res.json();
          setPortalData(data);
        } else {
          // Fallback to old endpoints if portal-detail not available
          const [profileRes, jobsRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/customers/me`, { headers }),
            fetch(`${BACKEND_URL}/api/jobs`, { headers }),
          ]);
          const customer = profileRes.ok ? await profileRes.json() : {
            id: currentUser.id || currentUser.username,
            name: currentUser.full_name || currentUser.name,
            email: currentUser.username || currentUser.email,
            phone: currentUser.phone_number || currentUser.phone,
            carPlate: currentUser.car_plate || currentUser.carPlate,
            carDetails: currentUser.car_model || currentUser.carDetails,
            bonusBalance: currentUser.bonus_balance || currentUser.bonusBalance || 0,
          };
          const jobsData = jobsRes.ok ? await jobsRes.json() : { jobs: [] };
          setPortalData({ customer, jobs: jobsData.jobs || [], inspections: {}, photos: {}, controlAppointments: [], vehicles: [] });
        }
      } catch (error) {
        toast({ title: "Hata", description: "Veriler yuklenemedi.", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [navigate]);

  const customer = portalData?.customer;
  const jobs = portalData?.jobs || [];
  const inspections = portalData?.inspections || {};
  const photos = portalData?.photos || {};
  const controls = portalData?.controlAppointments || [];
  const vehicles = portalData?.vehicles || [];

  const sortedJobs = useMemo(() => [...jobs].sort((a, b) => new Date(b.startDate || b.createdAt || 0) - new Date(a.startDate || a.createdAt || 0)), [jobs]);
  const activeJobs = useMemo(() => sortedJobs.filter(j => !['teslim_edildi', 'completed'].includes(normalizeStatus(j.status))), [sortedJobs]);
  const completedJobs = useMemo(() => sortedJobs.filter(j => ['teslim_edildi', 'completed'].includes(normalizeStatus(j.status))), [sortedJobs]);

  const selectedJob = jobs.find(j => j.id === selectedJobId);
  const selectedJobInspection = selectedJob ? inspections[selectedJob.id] : null;
  const selectedJobPhotos = selectedJob ? (photos[selectedJob.id] || []) : [];

  const totalDebt = useMemo(() => jobs.reduce((sum, j) => sum + (j.remainingBalance || 0), 0), [jobs]);

  // Group jobs by vehicle for service history
  const vehicleHistory = useMemo(() => {
    const map = {};
    for (const j of sortedJobs) {
      const key = j.carPlate || 'Bilinmiyor';
      if (!map[key]) map[key] = [];
      map[key].push(j);
    }
    return map;
  }, [sortedJobs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Yukleniyor...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <p className="text-gray-400">Profil bilgileri alinamadi.</p>
      </div>
    );
  }

  const JobCard = ({ job, isCompleted }) => {
    const hasWarranty = (job.warrantyInfo || []).some(w => w.status === 'active' || w.status === 'expiring_soon');
    return (
      <Card className={`border transition-all cursor-pointer hover:border-brand/60 ${isCompleted ? 'bg-gray-800/40 border-gray-700/30' : 'bg-gray-800/60 border-gray-700/50'}`}
        onClick={() => setSelectedJobId(job.id)} data-testid={`job-card-${job.id}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="text-white text-xs" style={{ backgroundColor: getStatusColor(job.status) }}>
                {getStatusLabel(job.status)}
              </Badge>
              {hasWarranty && <Badge className="bg-green-900/50 text-green-400 text-xs"><Shield className="h-3 w-3 mr-1" />Garantili</Badge>}
              <span className="text-gray-500 text-xs">{job.carPlate}</span>
              <span className="text-gray-500 text-xs">{job.startDate ? new Date(job.startDate).toLocaleDateString('tr-TR') : ''}</span>
            </div>
            <div className="text-right flex items-center gap-2">
              <div>
                <span className="text-white font-bold">{(job.totalPrice || 0).toLocaleString('tr-TR')} TL</span>
                {(job.remainingBalance || 0) > 0 && <span className="text-red-400 text-xs ml-1">(-{job.remainingBalance.toLocaleString('tr-TR')})</span>}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-500" />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(job.services || []).map((s, i) => (
              <Badge key={`svc-${s}-${i}`} variant="outline" className={`text-xs ${isCompleted ? 'border-gray-600 text-gray-400' : 'border-brand/50 text-brand'}`}>{s}</Badge>
            ))}
          </div>
          {/* Show brands inline */}
          {job.serviceDetails?.some(sd => sd.brand || sd.product) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {job.serviceDetails.filter(sd => sd.brand || sd.product).map((sd, i) => (
                <span key={`sdp-${sd.name}-${i}`} className="text-xs text-gray-500">{[sd.brand, sd.product].filter(Boolean).join(' / ')}</span>
              ))}
            </div>
          )}
          {!isCompleted && <StatusTimeline status={job.status} statusHistory={job.statusHistory} />}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="hairline-b" style={{ background: 'hsl(0 0% 7%)' }} data-testid="impersonation-banner">
          <div className="max-w-4xl mx-auto px-5 h-10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[11px]">
              <Eye size={11} className="text-brand" />
              <span className="text-muted-foreground font-mono uppercase tracking-wider">Musteri Gorunumu:</span>
              <span className="font-medium">{customer?.name || ''}</span>
            </div>
            <button onClick={handleExitImpersonation}
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 font-mono uppercase tracking-wider"
              data-testid="exit-impersonation-btn">
              <ArrowLeft size={10} /> Admin'e don
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="hairline-b">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <img src="/art-garage-logo.png" alt="ART Garage" className="h-8 w-auto" />
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/profile')} size="sm"
              className="hairline text-muted-foreground text-xs h-8 hover:bg-white/5 bg-transparent">
              <Eye className="h-3 w-3 mr-1" /> Profilim
            </Button>
            <button onClick={handleLogout}
              className="w-9 h-9 rounded-lg hairline hover:bg-white/5 flex items-center justify-center text-muted-foreground"
              style={{ background: 'hsl(0 0% 7%)' }}>
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 pt-10 pb-24 grid-bg-subtle">
        {/* Hero */}
        <div className="mb-8 fade-up">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand mb-2">-- Hos geldin</div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-[-0.03em] leading-[0.95]" data-testid="customer-dashboard-title">
            Merhaba, {customer.name?.split(' ')[0]}
          </h1>
          {vehicles.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-muted-foreground">
              <Car size={14} />
              <span className="font-mono text-sm tracking-wider">
                {vehicles.map(v => v.plate).join(', ')}
              </span>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { label: 'Bonus',      value: customer.bonusBalance || 0, unit: 'TL', color: 'hsl(var(--brand))', testid: 'bonus-balance' },
            { label: 'Kalan Borc', value: totalDebt,                   unit: 'TL', color: '#10b981',           testid: 'remaining-debt' },
            { label: 'Aktif Is',   value: activeJobs.length,           unit: '',   color: '#06b6d4',           testid: null },
            { label: 'Tamamlanan', value: completedJobs.length,        unit: '',   color: '#A8A8B3',           testid: 'total-jobs' },
          ].map((s, i) => (
            <div key={s.label} className="card-premium rounded-2xl p-5 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-3">{s.label}</div>
              <div className="flex items-baseline gap-1">
                <div className="font-mono font-semibold text-[26px] leading-none" style={{ color: s.color }}
                  data-testid={s.testid || undefined}>
                  {new Intl.NumberFormat('tr-TR').format(Number(s.value))}
                </div>
                {s.unit && <span className="font-mono text-xs text-muted-foreground">{s.unit}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs: Active / Completed / Service History */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex gap-1 hairline rounded-lg p-1 mb-6 w-fit" style={{ background: 'hsl(0 0% 7%)' }}>
            {[
              { key: 'active', label: 'Aktif', count: activeJobs.length, testid: 'tab-active' },
              { key: 'completed', label: 'Tamamlanan', count: completedJobs.length, testid: 'tab-completed' },
              { key: 'history', label: 'Servis Gecmisi', count: sortedJobs.length, testid: 'tab-history' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={t.testid}
                className={`px-4 h-8 rounded text-[12px] font-medium transition-colors flex items-center gap-1.5 ${
                  activeTab === t.key
                    ? 'bg-brand text-black'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
                <span className={`font-mono text-[10px] ${activeTab === t.key ? 'text-black/70' : 'text-muted-foreground'}`}>{t.count}</span>
              </button>
            ))}
          </div>

          {/* Active Jobs */}
          <TabsContent value="active">
            {activeJobs.length > 0 ? (
              <div className="space-y-3">
                {activeJobs.map(job => <JobCard key={job.id} job={job} isCompleted={false} />)}
              </div>
            ) : (
              <Card className="bg-gray-800/60 border-gray-700/50">
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Su an aktif isleminiz bulunmuyor</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Completed Jobs */}
          <TabsContent value="completed">
            {completedJobs.length > 0 ? (
              <div className="space-y-3">
                {completedJobs.map(job => <JobCard key={job.id} job={job} isCompleted={true} />)}
              </div>
            ) : (
              <Card className="bg-gray-800/60 border-gray-700/50">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Henuz tamamlanan isleminiz bulunmuyor</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Service History by Vehicle */}
          <TabsContent value="history">
            {Object.keys(vehicleHistory).length > 0 ? (
              <div className="space-y-6">
                {Object.entries(vehicleHistory).map(([plate, vJobs]) => {
                  const veh = vehicles.find(v => v.plate === plate);
                  return (
                    <div key={plate} data-testid={`vehicle-history-${plate}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Car className="h-5 w-5 text-brand" />
                        <h3 className="text-white font-semibold text-base">{plate}</h3>
                        {veh && <span className="text-gray-400 text-xs">{[veh.brand, veh.model, veh.color].filter(Boolean).join(' ')}</span>}
                        <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs ml-auto">{vJobs.length} islem</Badge>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-gray-700/50">
                        {vJobs.map(j => (
                          <div key={j.id} className="bg-gray-800/40 rounded-lg p-3 border border-gray-700/30 cursor-pointer hover:border-brand/40 transition-all ml-3"
                            onClick={() => setSelectedJobId(j.id)} data-testid={`history-job-${j.id}`}>
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className="text-white text-[10px]" style={{ backgroundColor: getStatusColor(j.status) }}>
                                  {getStatusLabel(j.status)}
                                </Badge>
                                <span className="text-gray-400 text-xs">{j.startDate ? new Date(j.startDate).toLocaleDateString('tr-TR') : ''}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-white font-semibold text-sm">{(j.totalPrice || 0).toLocaleString('tr-TR')} TL</span>
                                <ChevronRight className="h-3 w-3 text-gray-500" />
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {(j.services || []).map((s, si) => (
                                <span key={`vh-s-${s}-${si}`} className="text-xs text-gray-400">{s}{si < j.services.length - 1 ? ',' : ''}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Card className="bg-gray-800/60 border-gray-700/50">
                <CardContent className="py-12 text-center">
                  <Car className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">Henuz servis kaydiniz bulunmuyor</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Empty state */}
        {jobs.length === 0 && (
          <Card className="bg-gray-800/60 border-gray-700/50 mt-4">
            <CardContent className="py-12 text-center">
              <Car className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Henuz servis kaydiniz bulunmuyor</p>
              <p className="text-gray-500 text-sm mt-1">Ilk isleminiz olusturuldugunda burada gorunecektir</p>
            </CardContent>
          </Card>
        )}

        {/* Contact CTA for ready jobs */}
        {activeJobs.some(j => ['teslime_hazir', 'ready'].includes(normalizeStatus(j.status))) && (
          <Card className="bg-gradient-to-r from-green-900/40 to-green-800/20 border-green-700/50 mt-4" data-testid="ready-notification">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-green-400 font-semibold">Araciniz teslime hazir!</p>
                <p className="text-gray-400 text-sm">Teslim almak icin bizi arayabilirsiniz.</p>
              </div>
              <a href="tel:+905414220278">
                <Button className="bg-green-600 hover:bg-green-700 text-white"><Phone className="h-4 w-4 mr-2" />Ara</Button>
              </a>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Job Detail Modal */}
      <JobDetailModal
        job={selectedJob}
        inspection={selectedJobInspection}
        jobPhotos={selectedJobPhotos}
        token={token}
        controls={controls}
        onClose={() => setSelectedJobId(null)}
      />
    </div>
  );
};

export default CustomerDashboard;
